import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

const EDGE_FUNCTION_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-email`;

const sendWaitlistConfirmationEmail = async (to, name) => {
  try {
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'waitlist-confirmation',
        to,
        data: { name },
        resendApiKey: import.meta.env?.VITE_RESEND_API_KEY,
      }),
    });
    const result = await response?.json();
    if (!response?.ok) {
      console.warn('[WaitlistForm] Confirmation email failed:', result?.error);
    }
  } catch (err) {
    console.warn('[WaitlistForm] Confirmation email error:', err?.message);
  }
};

const WaitlistForm = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email?.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const { error: insertError } = await supabase?.from('waitlist')?.insert({ email: email?.trim()?.toLowerCase(), name: name?.trim() || null });

      if (insertError) {
        if (insertError?.code === '23505') {
          // Duplicate email — treat as success so user isn't confused
          setSubmitted(true);
        } else {
          setError('Something went wrong. Please try again.');
          console.error('[WaitlistForm] Insert error:', insertError);
        }
        setLoading(false);
        return;
      }

      // Send confirmation email (non-blocking)
      sendWaitlistConfirmationEmail(email?.trim()?.toLowerCase(), name?.trim() || null);

      setLoading(false);
      setSubmitted(true);
    } catch (err) {
      console.error('[WaitlistForm] Unexpected error:', err);
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <section id="waitlist" className="py-20" style={{ background: 'linear-gradient(135deg, #fff5f0 0%, #fff0f5 100%)' }}>
      <div className="max-w-2xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5"
            style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)', boxShadow: '0 4px 14px rgba(245,138,84,0.3)' }}
          >
            <Icon name="Bell" size={14} color="white" />
            <span className="text-white text-sm font-semibold">Stay in the Loop</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Join the Waitlist
          </h2>
          <p className="text-text-secondary text-lg leading-relaxed">
            Stackd is in beta testing. Register your interest and we'll notify you the moment we're ready to launch — plus you'll get early access.
          </p>
        </div>

        {submitted ? (
          <div className="bg-white rounded-2xl border border-border p-10 text-center shadow-sm">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
              style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
            >
              <Icon name="CheckCircle" size={32} color="white" />
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-3">You're on the list!</h3>
            <p className="text-text-secondary text-base leading-relaxed">
              Thanks for registering. We'll send you an update at <strong className="text-foreground">{email}</strong> when Stackd is ready for you.
            </p>
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-text-secondary">
              <Icon name="Shield" size={14} color="#F06F6B" />
              <span>No spam.{' '}
                <Link
                  to={`/unsubscribe?email=${encodeURIComponent(email)}`}
                  className="underline hover:opacity-80 transition-opacity"
                  style={{ color: '#F06F6B' }}
                >
                  Unsubscribe anytime.
                </Link>
              </span>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-border p-8 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name field */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Your Name <span className="text-text-secondary font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Icon name="User" size={16} color="#F06F6B" />
                  </div>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e?.target?.value)}
                    placeholder="Jane Smith"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-text-secondary focus:outline-none focus:ring-2 transition-all duration-200 text-sm"
                    style={{ '--tw-ring-color': '#F58A54' }}
                  />
                </div>
              </div>

              {/* Email field */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Icon name="Mail" size={16} color="#F06F6B" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e?.target?.value); setError(''); }}
                    placeholder="you@example.com"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-text-secondary focus:outline-none focus:ring-2 transition-all duration-200 text-sm"
                    style={{ '--tw-ring-color': '#F58A54' }}
                    required
                  />
                </div>
                {error && (
                  <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                    <Icon name="AlertCircle" size={12} />
                    {error}
                  </p>
                )}
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl font-semibold text-white transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5 shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0 flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Registering...</span>
                  </>
                ) : (
                  <>
                    <Icon name="Bell" size={16} />
                    <span>Join the Waitlist</span>
                  </>
                )}
              </button>

              {/* Trust note */}
              <p className="text-center text-xs text-text-secondary flex items-center justify-center gap-1.5">
                <Icon name="Lock" size={12} />
                Your information is private. We'll only contact you about Stackd updates.
              </p>
            </form>

            {/* Beta disclaimer */}
            <div className="mt-6 pt-6 border-t border-border">
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                <Icon name="FlaskConical" size={16} color="#d97706" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-800 text-xs font-semibold mb-1">Beta Testing Disclaimer</p>
                  <p className="text-amber-700 text-xs leading-relaxed">
                    Stackd is currently in closed beta. The platform is under active development — features, pricing, and availability are subject to change. By registering, you acknowledge the beta status and agree to receive launch communications.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default WaitlistForm;
